/**
 * Ein Packstück anlegen -- jeder Benutzer, nur mit Netz (ADR 0013 §7, ADR 0016).
 *
 * Die Ausnahme von „alles durch die Warteschlange“ (CLAUDE.md §3.3), wie beim
 * Stammplatz: Offline angelegt, könnten zwei Handys dieselbe Kennung vergeben
 * und schon Etiketten drucken. Ohne Netz sind die Knöpfe gesperrt und sagen
 * warum. Der Server legt Lagerort und Artikel an und bucht das Packstück an
 * seinem Standort ein -- mit Kennung und Zeitpunkt von diesem Gerät.
 */

import type { PackstueckAngelegt } from "@thw/contracts";
import { kennungVorschlaege, naechsteKennung, type KennungVorschlag } from "@thw/core-domain";
import { anfrage, geraeteKennung } from "../api/klient.js";
import { neueBuchungsKennung } from "../api/warteschlange.js";
import { packstueckUebernehmen } from "./abgleich.js";
import type { Bestand } from "./bestand.js";

/** Die nächsten freien Kennungen aus der lokalen Kopie -- geprüft wird beim Anlegen. */
export function vorschlaegeFuer(bestand: Bestand): KennungVorschlag[] {
  const belegt = new Set<string>();
  for (const l of bestand.lagerorte.values()) belegt.add(l.kennung);
  for (const a of bestand.artikel.values()) belegt.add(a.inventarnummer);
  const packstuecke = [...bestand.lagerorte.values()].filter((l) => l.artikel_id !== null);
  return kennungVorschlaege(packstuecke, belegt);
}

/** Die nächste freie Kennung eines Packstücktyps: nach GB-08 kommt GB-09 (ADR 0028). */
export function kennungFuerTyp(bestand: Bestand, kuerzel: string): string {
  const belegt: string[] = [];
  for (const l of bestand.lagerorte.values()) belegt.push(l.kennung);
  for (const a of bestand.artikel.values()) belegt.push(a.inventarnummer);
  return naechsteKennung(kuerzel, belegt);
}

/** Welcher Artikel diese THW-Nummer schon trägt -- dann wird er das Packstück. */
export function artikelMitNummer(bestand: Bestand, nummer: string) {
  for (const a of bestand.artikel.values()) if (a.inventarnummer === nummer) return a;
  return null;
}

export interface NeuesPackstueck {
  kennung: string;
  bezeichnung: string;
  /** Die THW-Inventarnummer, falls es eine gibt. Sonst dient die Kennung als Nummer. */
  inventarnummer: string | null;
  /** Der Packstücktyp, aus dem die Kennung kommt (ADR 0028). */
  packstueck_typ: string | null;
  /** Standort und Stammplatz. */
  ort_id: string;
}

/**
 * Legt an und übernimmt das Ergebnis sofort in die lokale Kopie. Ohne Netz
 * wirft es, und nichts ist angelegt.
 */
export async function packstueckAnlegen(neu: NeuesPackstueck): Promise<PackstueckAngelegt> {
  if (!globalThis.navigator.onLine) {
    throw new Error("Ein Packstück anlegen geht nur mit Netz.");
  }
  let antwort: PackstueckAngelegt;
  try {
    antwort = await anfrage<PackstueckAngelegt>(
      "POST",
      "/api/core/packstuecke",
      {
        ...neu,
        notiz: null,
        buchung_id: neueBuchungsKennung(),
        erfasst_am: new Date().toISOString(),
        geraet: geraeteKennung(),
      },
      15_000,
    );
  } catch (fehler) {
    // `fetch` ohne Verbindung und das Zeitlimit: Das Handy hält sich für
    // online, der Server ist aber nicht zu erreichen.
    if (fehler instanceof TypeError || fehler instanceof DOMException) {
      throw new Error("Keine Verbindung zum Server — das Packstück ist nicht angelegt.", {
        cause: fehler,
      });
    }
    throw fehler;
  }
  await packstueckUebernehmen(antwort.lagerort, antwort.artikel);
  return antwort;
}
